import { ProMessagingService } from '../pro-messaging.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { MessagingGroupModel } from '../../api/models/messaging-group-model';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { MessageModel } from '../../api/models/message-model';
import { PhoneNumbersUtils } from '../../core/utilities/phone-numbers-helpers';
import { NotificationModel } from '../../api/models/notification-model';
import { EventEmitter, Injectable } from '@angular/core';
import { ContactLookupModel } from '../../api/models/contact-lookup-model';

export interface FilterOptions {
  phoneNumber?: string;
  threadKey?: string;
  searchText?: string;
  sortType: 'sortByDateAndUnread' | 'sortByDate' | string;
}

@Injectable()
export class ProPhoneNumberGroupV2Datasource implements DataSource<MessagingGroupModel | undefined> {
  private readonly TAG = '[ProPhoneNumberGroupV2Component]';
  private _length = 0;
  private _pageSize = 100;
  private _currentPage = 0;
  private _cachedData = Array.from<MessagingGroupModel>({ length: 0 });
  private _fetchedPages = new Set<number>();
  private _dataStream = new BehaviorSubject<(MessagingGroupModel | undefined)[]>(this._cachedData);
  private _subscription = new Subscription();
  private _newMessageSubscription = new Subscription();
  private _deleteMessageSubscription = new Subscription();
  private _newNotificationsSubscription = new Subscription();
  totalPinned = 0;
  selectedThreadChangeEvent = new EventEmitter<MessagingGroupModel>();
  filterOptions: FilterOptions = { sortType: 'sortByDateAndUnread' };

  constructor(public messagingService: ProMessagingService) {}

  connect(collectionViewer: CollectionViewer): Observable<(MessagingGroupModel | undefined)[]> {
    this._subscription = collectionViewer.viewChange.subscribe(async range => {
      console.log(`${this.TAG} range subscription`, range);
      const startPage = this._getPageForIndex(range.start);
      const endPage = this._getPageForIndex(range.end - 1);
      for (let i = startPage; i <= endPage; i++) {
        await this._fetchPage(i);
      }
    });

    this._newMessageSubscription = this.messagingService.onNewMessageSubject.subscribe(async message => {
      await this._updateGroups(message);
    });

    this._deleteMessageSubscription = this.messagingService.onDeletedMessageSubject.subscribe(async message => {
      console.log(`${this.TAG} onDeleteMessageSubject`, message);
      if (!message) {
        return;
      }

      const group = this._cachedData.find(x => x.threadKey === message.threadKey);
      if (group && group.lastTime === message.time) {
        group.lastMessage = '';
        this._dataStream.next(this._cachedData);
      }
    });

    this._newNotificationsSubscription = this.messagingService.onNewNotificationsSubject.subscribe(
      async notifications => {
        await this._updateGroupsBadges(notifications);
      },
    );

    return this._dataStream;
  }

  disconnect(): void {
    console.log(`${this.TAG} disconnect`);
    this._subscription?.unsubscribe();
    this._newMessageSubscription?.unsubscribe();
    this._deleteMessageSubscription?.unsubscribe();
    this._newNotificationsSubscription?.unsubscribe();

    this._length = 0;
    this._fetchedPages = new Set<number>();

    this._cachedData = Array.from({ length: 0 });
    this._dataStream.next(this._cachedData);
  }

  async resetData(): Promise<void> {
    console.log(`${this.TAG} resetData`);
    await this._refreshData(0, true);
  }

  async updatePin(threadKey: string, pin: boolean): Promise<void> {
    console.log(`${this.TAG} updatePin`, threadKey, pin);
    const group = this._cachedData.find(x => x.threadKey === threadKey);
    if (group) {
      group.pin = pin;
      this.totalPinned = this._cachedData.filter(x => x?.pin).length;
      this.sortGroups();
      this._dataStream.next(this._cachedData);
    }
  }

  async refreshCurrentPages() {
    console.log(`${this.TAG} refreshCurrentPages`);
    await this._refreshData(this._currentPage, true);
    this._dataStream.next(this._cachedData);
  }

  onFilterDataChanged(filterOptions: FilterOptions): void {
    console.log(`${this.TAG} onFilterDataChanged`, filterOptions);

    this.filterOptions = filterOptions;
    this._refreshData(0, true).then();
  }

  onSortTypeChanged(sortType: 'sortByDateAndUnread' | 'sortByDate' | string) {
    console.log(`${this.TAG} onSortTypeChanged`, sortType);
    this.filterOptions.sortType = sortType;
    this.sortGroups();
    this._dataStream.next(this._cachedData);
  }

  onThreadKeyChanged(threadKey: string) {
    console.log(`${this.TAG} onThreadKeyChanged`, threadKey);
    this.filterOptions.threadKey = threadKey;

    if (this.filterOptions.threadKey === 'new') {
      return;
    }

    let selectedGroup = this._cachedData.find(x => x.threadKey === (this.filterOptions.threadKey || 'undefined'));

    if (!selectedGroup) {
      this._refreshData(this._currentPage, true).then();
    } else {
      this.selectGroup();
    }
  }

  private _getPageForIndex(index: number): number {
    this._currentPage = Math.floor(index / this._pageSize);
    return this._currentPage;
  }

  private async _refreshData(page: number, resetPages: boolean): Promise<void> {
    console.log(`${this.TAG} _refreshData`, page, resetPages);
    if (resetPages) {
      this._fetchedPages = new Set<number>();
      this._length = 0;
      this._cachedData = Array.from({ length: 0 });
    }

    await this._fetchPage(page);
    this.selectGroup();
  }

  private selectGroup() {
    console.log(`${this.TAG} selectGroup`, this.filterOptions.threadKey, this.messagingService.isComposing);
    if (!this.messagingService.isComposing) {
      let selectedGroup = this._cachedData.find(x => x.threadKey === (this.filterOptions.threadKey || 'undefined'));

      if (!selectedGroup && !this.filterOptions.threadKey) {
        selectedGroup = this._cachedData[0];
      }

      if (selectedGroup) {
        this.selectedThreadChangeEvent.emit(selectedGroup);
      } else {
        console.log(`${this.TAG} selectedGroup not selected`, this.filterOptions.threadKey);
      }

      console.log(`${this.TAG} selectedGroup`, selectedGroup, this.filterOptions.threadKey);
    } else {
      console.log(`${this.TAG} is composing`);
    }
  }

  private async _updateGroupsBadges(notifications: NotificationModel[]) {
    console.log(`${this.TAG} updateGroupsBadges: ` + notifications.length);
    let notificationsChanged = false;
    for (const group of this._cachedData.filter(x => !!x)) {
      const notification = notifications.find(
        x =>
          (x.eventType === 'pro-sms' || x.eventType === 'whatsapp') &&
          x.tnTo === this.filterOptions.phoneNumber &&
          x.thread === group.threadKey,
      );

      notificationsChanged = group.unread !== notification?.badge;

      if (notification) {
        group.unread = notification.badge;
      } else {
        group.unread = 0;
      }
    }

    if (notificationsChanged) {
      this.sortGroups();
      this._dataStream.next(this._cachedData);
    }
  }

  private async _updateGroups(message: MessageModel) {
    if (!message) {
      return;
    }

    const mainPhoneNumber =
      message.direction === 'out'
        ? PhoneNumbersUtils.phoneNumberToNational(message.tnFrom)
        : PhoneNumbersUtils.phoneNumberToNational(message.tnTo);

    if (!this.filterOptions.phoneNumber || mainPhoneNumber !== this.filterOptions.phoneNumber) {
      return;
    }

    console.log(`${this.TAG} Update phone number groups`);

    const threadKey = message.threadKey;

    const existingGroup = this._cachedData.find(x => !!x && x.threadKey === threadKey);

    if (!existingGroup) {
      console.log(`${this.TAG} New group: `, message);
      const newGroup = this.createGroupModel(message);
      this._cachedData.unshift(newGroup);
      this._length = this._cachedData.length;
      this.sortGroups();
      this._dataStream.next(this._cachedData);

      //await this._refreshData(this._currentPage, true);
    } else {
      let messageText = message.messageText;
      if (!messageText && message.media.length > 0) {
        messageText = message.direction === 'out' ? 'Media sent' : 'Media received';
      }
      existingGroup.threadKey = threadKey;
      existingGroup.primaryNumber = mainPhoneNumber;
      existingGroup.lastTime = message.time;
      existingGroup.lastMessage = messageText;
      existingGroup.groupNumbers = message.groupNumbers;
      existingGroup.broadcastId = message.broadcastId;

      if (message.wabaConvId) {
        existingGroup.wabaConvId = message.wabaConvId;
      }
      if (message.wabaConvExp) {
        existingGroup.wabaConvExp = message.wabaConvExp;
      }

      this.sortGroups();
      this._dataStream.next(this._cachedData);
    }
  }

  private async _fetchPage(page: number) {
    console.log(`${this.TAG} _fetchPage ---- Datasource page: ` + page);
    if (this._fetchedPages.has(page)) {
      return;
    }
    this._fetchedPages.add(page);

    if (!this.filterOptions.phoneNumber) {
      this._cachedData = Array.from<MessagingGroupModel>({ length: 0 });
      this._dataStream.next(this._cachedData);
      return;
    }

    const request = {
      phoneNumber: this.filterOptions.phoneNumber,
      searchText: this.filterOptions.searchText,
      limit: this._pageSize,
      skip: this._pageSize * page,
      forceUpdate: false,
    };

    console.log(`${this.TAG} _fetchPage ---- get messaging threads page:` + page, request);
    const groups = await this.messagingService.queryPhoneNumberThreads(request);
    console.log(`${this.TAG} _fetchPage ---- after get messaging threads total:` + groups.length, request);

    if (this._length === 0) {
      this._length = this.messagingService.totalGroups;
      this._cachedData = Array.from<MessagingGroupModel>({ length: this._length });
    }

    this._length = this.messagingService.totalGroups;
    this._cachedData.splice(request.skip, groups.length, ...groups);

    this.totalPinned = this._cachedData.filter(x => x?.pin).length;

    this.sortGroups();
    this._dataStream.next(this._cachedData);
  }

  sortGroups() {
    console.log(`${this.TAG} sortGroups ${this.filterOptions.sortType}`);
    this._cachedData.sort((a, b) => {
      // Convert lastTime to Date objects
      const aTime = new Date(a.lastTime || '');
      const bTime = new Date(b.lastTime || '');

      // Determine pinned status (1 if pinned, 0 if not)
      const aPinned = a.pin ? 1 : 0;
      const bPinned = b.pin ? 1 : 0;

      // Determine unread status (1 if unread, 0 if not)
      const aUnread = a.unread > 0 ? 1 : 0;
      const bUnread = b.unread > 0 ? 1 : 0;

      // If pinned statuses are different, prioritize pinned items
      if (aPinned !== bPinned) {
        return bPinned - aPinned; // Higher priority for pinned
      }

      // Sorting by "sortByDateAndUnread" criteria
      if (this.filterOptions.sortType === 'sortByDateAndUnread') {
        // If unread statuses are different, prioritize unread items
        if (aUnread !== bUnread) {
          return bUnread - aUnread; // Higher priority for unread
        }
      }

      // Default sorting by date (newest first)
      return bTime.getTime() - aTime.getTime();
    });
}

  private createGroupModel(message: MessageModel): MessagingGroupModel {
    const primaryNumber = PhoneNumbersUtils.phoneNumberToInternational(
      message.direction === 'in' ? message.tnTo : message.tnFrom,
    );
    let messageText = message.messageText;
    if (!messageText && message.media.length > 0) {
      messageText = message.direction === 'out' ? 'Media sent' : 'Media received';
    }

    return {
      threadKey: message.threadKey,
      primaryNumber: primaryNumber,
      lastTime: message.time,
      lastMessage: messageText,
      groupNumbers: message.groupNumbers,
      broadcastId: message.broadcastId,
      unread: message.direction === 'in' ? 1 : 0,
      pin: false,
      groupKey: message.groupKey,
      isGroup: message.groupNumbers.length > 2,
      isBroadcast: !!message.broadcastId,
      wabaConvId: message.wabaConvId,
      wabaConvExp: message.wabaConvExp,
      updatedOn: message.updatedOn,
      rowNumber: -99,
      contacts: message.groupNumbers
        .filter(x => x != primaryNumber)
        .map(x => {
          return {
            id: '0',
            isOptedIn: true,
            communicationType: 0,
            phoneNumber: x,
            phoneType: 'Contacts',
          } as ContactLookupModel;
        }),
    };
  }
}
